import { STRIPE_PRODUCT_IDS } from '~/repub-config';
import { type RequestContext } from '~/types/RequestContext';

/**
 * Enum representing different subscription types
 */
export enum SubscriptionType {
  MONTHLY_ESSENTIAL = 'MONTHLY_ESSENTIAL',
  MONTHLY_PREMIUM = 'MONTHLY_PREMIUM',
  NONE = 'NONE',
  WHOSCORED_PLUS = 'WHOSCORED_PLUS',
  YEARLY_ESSENTIAL = 'YEARLY_ESSENTIAL',
  YEARLY_PREMIUM = 'YEARLY_PREMIUM',
}

/**
 * Gets the user's subscription types based on their active subscriptions.
 *
 * @param requestContext - The current request context containing user and environment information
 * @returns Array of subscription types the user has
 *
 * @example
 * ```typescript
 * const subscriptionTypes = getUserSubscriptionTypes(requestContext);
 * if (hasSubscriptionType(subscriptionTypes, SubscriptionType.MONTHLY_PREMIUM)) {
 *   // Handle monthly premium subscriber
 * }
 * ```
 */
/**
 * Mapping of Stripe product IDs to subscription types
 */
const PRODUCT_ID_TO_SUBSCRIPTION_TYPE: Record<string, SubscriptionType> = {
  [STRIPE_PRODUCT_IDS.ESSENTIAL_MONTHLY]: SubscriptionType.MONTHLY_ESSENTIAL,
  [STRIPE_PRODUCT_IDS.ESSENTIAL_ANNUALLY]: SubscriptionType.YEARLY_ESSENTIAL,
  [STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_MONTHLY]: SubscriptionType.MONTHLY_PREMIUM,
  [STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_THREE_MONTHLY]: SubscriptionType.MONTHLY_PREMIUM,
  [STRIPE_PRODUCT_IDS.PREMIUM_MONTHLY]: SubscriptionType.MONTHLY_PREMIUM,
  [STRIPE_PRODUCT_IDS.PREMIUM_ANNUALLY]: SubscriptionType.YEARLY_PREMIUM,
  [STRIPE_PRODUCT_IDS.WHOSCORED_PLUS_MONTHLY]: SubscriptionType.MONTHLY_PREMIUM,
};

/**
 * Checks if a subscription is active (ACTIVE or TRIAL status)
 */
const isActiveSubscription = (subscription: { status: string }): boolean => {
  return subscription.status === 'ACTIVE' || subscription.status === 'TRIAL';
};

export const getUserSubscriptionTypes = (requestContext: RequestContext): SubscriptionType[] => {
  const activeSubscriptions = requestContext.user.subscriptions.filter(isActiveSubscription);

  if (activeSubscriptions.length === 0) {
    return [SubscriptionType.NONE];
  }

  const subscriptionTypes = new Set<SubscriptionType>();

  for (const subscription of activeSubscriptions) {
    const productId = subscription.product?.id;
    if (productId && PRODUCT_ID_TO_SUBSCRIPTION_TYPE[productId]) {
      subscriptionTypes.add(PRODUCT_ID_TO_SUBSCRIPTION_TYPE[productId]);
    }
  }

  return subscriptionTypes.size > 0 ? Array.from(subscriptionTypes) : [SubscriptionType.NONE];
};

/**
 * Helper function to check if a user has a specific subscription type
 *
 * @param subscriptionTypes - Array of subscription types the user has
 * @param type - The subscription type to check for
 * @returns boolean indicating if the user has the specified subscription type
 */
export const userHasSubscriptionType = (subscriptionTypes: SubscriptionType[], type: SubscriptionType): boolean => {
  return subscriptionTypes.includes(type);
};

/**
 * Helper function to check if a user has any premium subscription
 *
 * @param subscriptionTypes - Array of subscription types the user has
 * @returns boolean indicating if the user has any premium subscription
 */
export const userHasAnyPremiumSubscription = (subscriptionTypes: SubscriptionType[]): boolean => {
  return (
    subscriptionTypes.includes(SubscriptionType.MONTHLY_PREMIUM) ||
    subscriptionTypes.includes(SubscriptionType.YEARLY_PREMIUM)
  );
};

/**
 * Helper function to check if a user has any essential subscription
 *
 * @param subscriptionTypes - Array of subscription types the user has
 * @returns boolean indicating if the user has any essential subscription
 */
export const userHasAnyEssentialSubscription = (subscriptionTypes: SubscriptionType[]): boolean => {
  return (
    subscriptionTypes.includes(SubscriptionType.MONTHLY_ESSENTIAL) ||
    subscriptionTypes.includes(SubscriptionType.YEARLY_ESSENTIAL)
  );
};

/**
 * Helper function to check if a user has any monthly subscription
 *
 * @param subscriptionTypes - Array of subscription types the user has
 * @returns boolean indicating if the user has any monthly subscription
 */
export const userHasAnyMonthlySubscription = (subscriptionTypes: SubscriptionType[]): boolean => {
  return (
    subscriptionTypes.includes(SubscriptionType.MONTHLY_ESSENTIAL) ||
    subscriptionTypes.includes(SubscriptionType.MONTHLY_PREMIUM)
  );
};

/**
 * Helper function to check if a user has any yearly subscription
 *
 * @param subscriptionTypes - Array of subscription types the user has
 * @returns boolean indicating if the user has any yearly subscription
 */
export const userHasAnyYearlySubscription = (subscriptionTypes: SubscriptionType[]): boolean => {
  return (
    subscriptionTypes.includes(SubscriptionType.YEARLY_ESSENTIAL) ||
    subscriptionTypes.includes(SubscriptionType.YEARLY_PREMIUM)
  );
};
